import { Component, OnDestroy, OnInit, signal, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { RideStatusType } from '../../../../shared/enums/rideStatusType';
import { RequestBuilderService } from '../../../../shared/services/request-builder.service';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { SelectModule } from 'primeng/select';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { interval, map, Observable, Subscription, takeWhile } from 'rxjs';
import { AuthService } from '../../../../core/auth/services/auth.service';

@Component({
  selector: 'app-cancel-transportation-dialog',
  standalone: true,
  imports: [FormsModule, ButtonComponent, AutoCompleteModule, SelectModule],
  templateUrl: './cancel-transportation-dialog.component.html',
  styleUrl: './cancel-transportation-dialog.component.scss',
})
export class CancelTransportationDialogComponent implements OnInit, OnDestroy {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly authService = inject(AuthService);

  loading = false;
  reason: string = '';
  suggestions: string[] = [`Per the father’s request.`, `Per the guardian’s request.`, `Per the mother’s request.`, `Per the school district’s request.`, `Per the teacher’s request.`];

  statusOptions = [
    { name: `Early Cancel`, id: RideStatusType.CanceledInAdvance },
    { name: `Late Cancel`, id: RideStatusType.CanceledLate },
  ];
  cancelTypeStatus = { name: `Early Cancel`, id: RideStatusType.CanceledInAdvance };
  subscription!: Subscription;
  timeToLateCancel = signal<{ hours: number; minutes: number; seconds: number } | null>(null);
  filteredSuggestions: string[] = [];

  appSettings = this.authService.appSetting();

  ngOnInit() {
    this.getCancellationType();

    this.filteredSuggestions = [...this.suggestions];
  }

  getCancellationType() {
    this.requestBuilder.get(`rides/${this.config.data.uuid}/cancellation-type`).subscribe(res => {
      let endTime = new Date(res.data.endTime);

      this.subscription = this.calculateCountdown(endTime).subscribe(time => {
        this.timeToLateCancel.set(time);
        this.timeToLateCancel();
      });
    });
  }

  calculateCountdown(endTime: Date): Observable<{ hours: number; minutes: number; seconds: number }> {
    return interval(1000).pipe(
      map(() => {
        const now = new Date();
        const timeDifference = endTime.getTime() - now.getTime();

        if (timeDifference < 1000) {
          this.cancelTypeStatus = { name: `Late Cancel`, id: RideStatusType.CanceledLate };
          return {
            hours: 0,
            minutes: 0,
            seconds: 0,
          };
        }

        const hours = Math.floor(timeDifference / (1000 * 60 * 60));
        const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

        return { hours, minutes, seconds };
      }),
      takeWhile(({ hours, minutes, seconds }) => hours > 0 || minutes > 0 || seconds > 0, true),
    );
  }

  filterSuggestions(event: any, autoComplete: AutoComplete) {
    if (this.reason) {
      this.filteredSuggestions = this.suggestions.filter(suggestion => suggestion.toLowerCase().includes(this.reason.toLowerCase()));
    } else {
      this.filteredSuggestions = [...this.suggestions];
      if (this.filteredSuggestions.length > 0) {
        autoComplete.show();
      }
    }
  }

  confirmAction(form: NgForm) {
    if (form?.invalid) return;

    this.loading = true;

    const body = {
      rideStatusId: this.cancelTypeStatus.id,
      internalNote: form.form.value.reason,
      isNotifyPartner: true,
      isChangeTransportationStatus: true,
      isNotifyFamilyAppUser: true,
    };
    this.requestBuilder.patch(`stop-cargoes/${this.config.data.passenger.stopCargoId}/status`, body).subscribe({
      next: () => {
        this.loading = false;
        this.ref.close({ confirmed: true });
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
